import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ChefHat, Square, Droplets, Sofa, Wind, Layers, Flame, Building2, MapPin, Shield, Clock, Users, Star, Quote, Phone, MessageCircle, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { SITE_STATS, FORMATTED_STATS } from '../constants/siteStats';
import { API } from '../App';

// Animated Counter Component - starts at end value for SEO, animates on visibility
const AnimatedCounter = ({ end, suffix = '', duration = 2000, isK = false, formatComma = false }) => {
  // Start at end value for SEO - search engines will see correct numbers
  const [count, setCount] = useState(end);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          // Reset to 0 and animate up when visible
          setCount(0);
          animateCount();
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated, end]);

  const animateCount = () => {
    const startTime = Date.now();
    const endValue = end;
    
    const updateCount = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentValue = Math.floor(easeOutQuart * endValue);
      
      setCount(currentValue);
      
      if (progress < 1) {
        requestAnimationFrame(updateCount);
      }
    };
    
    requestAnimationFrame(updateCount);
  };

  // Format the count
  const formatCount = () => {
    if (isK) {
      return `${Math.floor(count / 1000)}K`;
    }
    if (formatComma) {
      return count.toLocaleString();
    }
    return count;
  };

  return (
    <span ref={ref}>
      {formatCount()}{suffix}
    </span>
  );
};

// Default services data as fallback
const defaultServices = [
  { id: 'exhausts', title: 'Kitchen Exhaust, Duct & Roof Top Fan', description: 'Complete kitchen exhaust system cleaning including hoods, ducts, and rooftop fans. AS 1851:2012 compliant.', icon: 'Wind', features: ['Hood cleaning', 'Duct cleaning', 'Roof top fan', 'Fire safety'], path: '/services/canopy-duct-fan-cleaning-perth' },
  { id: 'window', title: 'Window Cleaning', description: 'Crystal clear windows for commercial properties. High-rise and ground-level specialists with streak-free results.', icon: 'Square', features: ['High-rise', 'Ground level', 'Streak-free', 'All glass types'], path: '/services/commercial-window-cleaning-perth' },
  { id: 'warehouse', title: 'Warehouse & Industrial Cleaning', description: 'Professional floor stripping and sealing services. Restore and protect your commercial floors.', icon: 'Layers', features: ['Floor stripping', 'Sealing', 'Polishing', 'Floor restoration'], path: '/services/warehouse-industrial-cleaning-perth' },
  { id: 'pressure-wash', title: 'Commercial Pressure Wash', description: 'Industrial-strength pressure washing for driveways, facades, parking lots, and outdoor areas.', icon: 'Droplets', features: ['Driveways', 'Building facades', 'Parking lots', 'Outdoor areas'], path: '/services/commercial-high-pressure-cleaning-perth' },
  { id: 'kitchen-clean', title: 'Commercial Kitchen Clean', description: 'Professional deep cleaning for commercial kitchens. We ensure your kitchen meets health and safety standards with thorough sanitisation.', icon: 'ChefHat', features: ['Deep cleaning', 'Sanitisation', 'Health compliance', 'Equipment cleaning'], path: '/services/commercial-kitchen-cleaning-perth' },
  { id: 'carpet', title: 'Carpet Cleaning', description: 'Professional carpet steam cleaning and dry cleaning. Stain removal and deodorising for commercial spaces.', icon: 'Sofa', features: ['Steam cleaning', 'Dry cleaning', 'Stain removal', 'Deodorising'], path: '/services/commercial-carpet-cleaning-perth' },
  { id: 'office-clean', title: 'Office Clean', description: 'Complete office cleaning solutions including desks, floors, kitchens, and bathrooms. Keep your workplace spotless and professional.', icon: 'Building2', features: ['Daily cleaning', 'Deep cleaning', 'Bathroom sanitisation', 'Kitchen areas'], path: '/services/commercial-office-cleaning-perth' },
  { id: 'pizza-oven', title: 'Pizza Oven Clean', description: 'Specialised cleaning for commercial pizza ovens. Remove carbon buildup and maintain optimal performance.', icon: 'Flame', features: ['Carbon removal', 'Deep cleaning', 'Performance maintenance', 'Safety compliance'], path: '/services/commercial-pizza-oven-cleaning-perth' }
];

const Home = ({ onQuoteClick, onBookingClick }) => {
  const [services, setServices] = useState(defaultServices);
  const [testimonials, setTestimonials] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);

  // FAQ data
  const faqs = [
    {
      question: 'How quickly can you start?',
      answer: 'We can usually commence services within 48 to 72 hours of receiving your booking confirmation.'
    },
    {
      question: 'Do you visit before giving a quote?',
      answer: 'Yes. We come to your site, look at exactly what needs cleaning and quote from that, so there are no surprises.'
    },
    {
      question: 'Do you service areas outside Perth?',
      answer: 'Yes. Alongside the Perth metro area, we regularly service Kalgoorlie, Esperance, Albany, Busselton, Bunbury, Margaret River, Australind and Geraldton.'
    },
    {
      question: 'What paperwork do I get?',
      answer: 'You get before and after photos of the work and, for kitchen exhaust cleaning, a certificate of completion documented to AS 1851:2012, ready for your insurer, council or next audit.'
    }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, testimonialsRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/testimonials`)
        ]);
        if (servicesRes.data && servicesRes.data.length > 0) {
          // Merge API data with defaultServices paths
          const servicesWithPaths = servicesRes.data.map(service => {
            const defaultService = defaultServices.find(ds => ds.id === service.id);
            return {
              ...service,
              path: defaultService?.path || '/services'
            };
          });
          setServices(servicesWithPaths);
        }
        if (testimonialsRes.data) {
          setTestimonials(testimonialsRes.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        // Keep using default services
      }
    };
    fetchData();
  }, []);

  const iconMap = {
    ChefHat: ChefHat,
    Square: Square,
    Droplets: Droplets,
    Sofa: Sofa,
    Wind: Wind,
    Layers: Layers,
    Flame: Flame,
    Building2: Building2
  };

  // Calculate years as Extreme Clean Australia (brand started 2006, business started 1999)
  const brandFoundingYear = 2006;
  const currentYear = new Date().getFullYear();
  const yearsExperience = currentYear - brandFoundingYear;

  const stats = [
    { value: yearsExperience, suffix: '+', label: 'Years Experience' },
    { value: SITE_STATS.activeClients, suffix: '', label: 'Active Clients' },
    { value: SITE_STATS.jobsCompletedThisYear, suffix: '', label: 'Jobs Completed This Year', formatComma: true }
  ];

  // LocalBusiness Schema for SEO
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://www.extreme-clean.com.au/#organization",
    "name": "Extreme Clean Australia",
    "image": "https://customer-assets.emergentagent.com/job_customer-portal-v2/artifacts/kd8dey33_logo_final-1_1_page-0001.jpeg",
    "url": "https://www.extreme-clean.com.au",
    "telephone": "+61861875523",
    "email": "info.eca@extreme-clean.com.au",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "18/92-94 Poole St",
      "addressLocality": "Welshpool",
      "addressRegion": "WA",
      "postalCode": "6106",
      "addressCountry": "AU"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "08:00",
        "closes": "17:00"
      }
    ],
    "areaServed": [
      "Perth", "Osborne Park", "Malaga", "Kewdale", "Welshpool", "Canning Vale", 
      "Bibra Lake", "Jandakot", "Wangara", "Balcatta", "Bayswater", "Rockingham", 
      "Mandurah", "Joondalup", "Midland", "Yanchep", "Leederville", "Northbridge", 
      "Mount Lawley", "Fremantle", "Cockburn", "Landsdale", "Hope Valley", 
      "Kalgoorlie", "Esperance", "Albany", "Busselton", "Bunbury", "Margaret River", "Australind", "Geraldton"
    ],
    "sameAs": [
      "https://maps.app.goo.gl/6UidyLJhWLaTNiyH6",
      "https://www.linkedin.com/in/extreme-clean-australia-419b0327",
      "https://www.instagram.com/extremecleanaustralia"
    ],
    "foundingDate": "2006",
    "description": "Professional commercial cleaning services across Western Australia. Kitchen exhaust, window, carpet, office and industrial cleaning."
  };

  return (
    <>
      <Helmet>
        <title>Commercial & Industrial Cleaning Perth | Extreme Clean Australia</title>
        <link rel="canonical" href="https://www.extreme-clean.com.au/" />
        <meta name="description" content={`Commercial and industrial cleaning across Perth and Western Australia. Kitchen exhaust, window, carpet, office and warehouse cleaning. 20+ years experience. ${FORMATTED_STATS.activeClients} active clients.`} />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }} />
      <div className="pt-24 lg:pt-44" data-testid="home-page">
      {/* Hero Section - Split layout like reference */}
      <section className="min-h-[90vh] flex items-center bg-white relative" data-testid="hero-section">
        {/* Right side - Commercial kitchen image */}
        <div className="absolute right-0 top-0 w-1/2 h-full hidden lg:block">
          <img 
            src="https://images.unsplash.com/photo-1588416820614-f8d6ac6cea56?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwcmVzdGF1cmFudCUyMGtpdGNoZW4lMjBzdGFpbmxlc3MlMjBzdGVlbHxlbnwwfHx8fDE3ODg5NDg5OTJ8MA&ixlib=rb-4.1.0&q=85"
            alt="Commercial kitchen with stainless steel equipment"
            className="w-full h-full object-cover"
            style={{ opacity: 1 }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/50 to-transparent" />
        </div>
        
        {/* Mobile background image */}
        <div className="absolute inset-0 lg:hidden">
          <img 
            src="https://images.unsplash.com/photo-1588416820614-f8d6ac6cea56?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwcmVzdGF1cmFudCUyMGtpdGNoZW4lMjBzdGFpbmxlc3MlMjBzdGVlbHxlbnwwfHx8fDE3ODg5NDg5OTJ8MA&ixlib=rb-4.1.0&q=85"
            alt="Commercial kitchen with stainless steel equipment"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full relative z-10">
          <div className="max-w-2xl">
            <div className="inline-block bg-[#3B82F6] text-white px-4 py-2 mb-6 animate-fadeInUp">
              <span className="text-xs font-mono uppercase tracking-wider">Serving Entire Western Australia</span>
            </div>
            
            <div className="animate-fadeInUp stagger-1">
              <h1 className="font-heading font-bold text-5xl md:text-6xl lg:text-7xl !leading-[1.4] mb-6 text-[#0A0A0A]">
                Trusted With
                <br />
                <span className="text-[#3B82F6]">Perth's Biggest</span>
                <br />
                Kitchens.
              </h1>
            </div>
            
            <p className="text-lg text-gray-600 mb-8 max-w-xl animate-fadeInUp stagger-2">
              From McDonald's to KFC to Domino's, commercial cleaning built for brands that can't afford to get it wrong.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 animate-fadeInUp stagger-3">
              <button 
                onClick={onQuoteClick}
                className="btn-primary flex items-center justify-center gap-2"
                data-testid="hero-quote-btn"
              >
                Get Free Quote
                <ArrowRight size={18} />
              </button>
              <a 
                href="tel:0861875523"
                className="btn-secondary flex items-center justify-center gap-2"
                data-testid="hero-call-btn"
              >
                <Phone size={18} />
                Call 08 6187 5523
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip - Directly under hero */}
      <section className="py-12 bg-gray-50 border-b border-gray-200" data-testid="trust-strip">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <h2 className="text-center text-lg md:text-xl font-semibold text-gray-700 mb-8">
            Cleaning for the brands you know
          </h2>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12 mb-6">
            <img 
              src="/images/clients/kfc-logo-2026.png" 
              alt="KFC logo" 
              className="h-12 md:h-14 w-auto object-contain"
              loading="lazy"
            />
            <img 
              src="/images/clients/mcdonalds.png" 
              alt="McDonald's logo" 
              className="h-12 md:h-14 w-auto object-contain"
              loading="lazy"
            />
            <img 
              src="/images/clients/dominos.png" 
              alt="Domino's logo" 
              className="h-12 md:h-14 w-auto object-contain"
              loading="lazy"
            />
            <img 
              src="/images/clients/pizza-hut.jpg" 
              alt="Pizza Hut logo" 
              className="h-12 md:h-14 w-auto object-contain"
              loading="lazy"
            />
            <img 
              src="/images/clients/costco.webp" 
              alt="Costco logo" 
              className="h-12 md:h-14 w-auto object-contain"
              loading="lazy"
            />
            <img 
              src="/images/clients/baptist-care.png" 
              alt="BaptistCare logo" 
              className="h-12 md:h-14 w-auto object-contain"
              loading="lazy"
            />
            <img 
              src="/images/clients/city-of-canning.webp" 
              alt="City of Canning logo" 
              className="h-12 md:h-14 w-auto object-contain"
              loading="lazy"
            />
          </div>
          <p className="text-center text-sm md:text-base text-gray-600">
            Cleaning for 60+ KFC stores, 40+ Domino's stores, 30+ McDonald's sites and 15+ Pizza Hut stores across Western Australia.
          </p>
        </div>
      </section>

      {/* Stats Section - BLACK */}
      <section className="bg-[#273268] py-16" data-testid="stats-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="stat-number text-4xl md:text-5xl text-[#3B82F6] mb-2">
                  <AnimatedCounter 
                    end={stat.value} 
                    suffix={stat.suffix} 
                    duration={2000}
                    isK={stat.isK}
                    formatComma={stat.formatComma}
                  />
                </p>
                <p className="text-gray-400 text-sm uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section - WHITE */}
      <section className="py-24 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12">
            <div>
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">What We Do</p>
              <h2 className="font-heading font-bold text-4xl md:text-5xl text-[#0A0A0A]">Our Services</h2>
            </div>
            <Link 
              to="/services" 
              className="mt-4 md:mt-0 flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-[#3B82F6] transition-colors"
              data-testid="view-all-services"
            >
              View All Services
              <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.slice(0, 8).map((service, index) => {
              const IconComponent = iconMap[service.icon] || Building2;
              return (
                <Link 
                  to={service.path || '/services'}
                  key={service.id} 
                  className="service-card group block"
                  data-testid={`service-card-${service.id}`}
                >
                  <div className="flex justify-between items-start mb-6">
                    <div className="w-12 h-12 bg-blue-50 flex items-center justify-center group-hover:bg-[#3B82F6] transition-all">
                      <IconComponent size={24} className="text-[#3B82F6] group-hover:text-white" />
                    </div>
                    <span className="font-mono text-xs text-gray-400">0{index + 1}</span>
                  </div>
                  <h3 className="font-heading font-bold text-xl mb-3 text-[#0A0A0A]">{service.title}</h3>
                  <p className="text-gray-600 text-sm mb-6 leading-relaxed">{service.description}</p>
                  <div className="flex justify-between items-center">
                    <ul className="flex flex-wrap gap-2">
                      {service.features.slice(0, 2).map((feature, i) => (
                        <li key={i} className="text-xs text-gray-500 bg-gray-100 px-2 py-1">{feature}</li>
                      ))}
                    </ul>
                    <ArrowRight size={18} className="text-gray-400 group-hover:text-[#3B82F6] transition-colors" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industrial Band - After Services */}
      <section className="py-20 bg-[#1a1a2e]" data-testid="industrial-band">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-8">
            Beyond the Kitchen: Warehouses, Industrial and Government Sites
          </h2>
          <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-10 max-w-4xl mx-auto">
            Extreme Clean Australia brings the same standards to warehouses, industrial sites and government buildings. That includes high-pressure cleaning, rotary concrete floor cleaning and extraction, carpet and window cleaning, dust removal, and end-of-lease warehouse turnovers. We already clean local government sites, including the City of Canning, and we are actively working toward ISO certification to take on larger government and mining contracts.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/services/warehouse-industrial-cleaning-perth"
              className="inline-flex items-center justify-center gap-2 bg-[#3B82F6] text-white px-6 py-3 font-semibold hover:bg-[#2563eb] transition-colors"
              data-testid="industrial-warehouse-link"
            >
              Warehouse & Industrial Cleaning
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/industries/government-building-cleaning-perth"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-6 py-3 font-semibold hover:bg-white hover:text-[#1a1a2e] transition-colors"
              data-testid="industrial-government-link"
            >
              Government Building Cleaning
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 bg-white" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-[#0A0A0A] mb-4">
              How It Works
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Clear communication from the first call to the finished job.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="relative">
              <div className="text-6xl font-bold text-[#3B82F6]/10 mb-4">01</div>
              <h3 className="font-heading font-bold text-xl mb-3 text-[#0A0A0A]">Get in touch</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Call or email us and we get back to you promptly to book a time to visit.
              </p>
            </div>
            
            <div className="relative">
              <div className="text-6xl font-bold text-[#3B82F6]/10 mb-4">02</div>
              <h3 className="font-heading font-bold text-xl mb-3 text-[#0A0A0A]">We visit your site</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                We meet you on site, look at exactly what needs cleaning, ask the right questions and take photos so the quote is accurate.
              </p>
            </div>
            
            <div className="relative">
              <div className="text-6xl font-bold text-[#3B82F6]/10 mb-4">03</div>
              <h3 className="font-heading font-bold text-xl mb-3 text-[#0A0A0A]">We agree a schedule</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                You get a clear quote and a cleaning schedule that suits your business, before hours, after hours or weekends.
              </p>
            </div>
            
            <div className="relative">
              <div className="text-6xl font-bold text-[#3B82F6]/10 mb-4">04</div>
              <h3 className="font-heading font-bold text-xl mb-3 text-[#0A0A0A]">We clean and keep you informed</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                You know when we are coming and what will be done. You get before and after photos and, for kitchen exhaust work, a certificate of completion documented to AS 1851:2012.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us - BLUE */}
      <section className="py-24 bg-[#3B82F6]" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-blue-200 font-mono text-sm uppercase tracking-wider mb-2">Why Choose Us</p>
              <h2 className="font-heading font-bold text-4xl md:text-5xl mb-8 text-white">
                Trusted Across Western Australia
              </h2>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Shield className="text-white" size={24} />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-lg mb-1 text-white">Fully Insured & Certified</h4>
                    <p className="text-blue-100 text-sm">All our staff are police checked, trained professionals covered by comprehensive insurance.</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="text-white" size={24} />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-lg mb-1 text-white">Flexible Scheduling</h4>
                    <p className="text-blue-100 text-sm">We work around your schedule - early morning, after hours, or weekends available.</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Users className="text-white" size={24} />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-lg mb-1 text-white">Dedicated Teams</h4>
                    <p className="text-blue-100 text-sm">Same cleaning team every visit, so they know your space and standards.</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-white/10 flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="text-white" size={24} />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-lg mb-1 text-white">Clear Communication</h4>
                    <p className="text-blue-100 text-sm">You know when we are booked in, what will be done and when it is finished, from the store manager through to head office.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1774031159721-aec9230f38db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxwcmVzc3VyZSUyMHdhc2hlciUyMGNsZWFuaW5nJTIwY29uY3JldGV8ZW58MHx8fHwxNzg4OTQ4OTkyfDA&ixlib=rb-4.1.0&q=85"
                alt="High pressure cleaning in action"
                className="w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#273268] text-white p-6">
                <p className="font-mono text-3xl font-bold">{new Date().getFullYear() - 2006}+</p>
                <p className="text-sm uppercase tracking-wider text-gray-400">Years of Excellence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Only show if testimonials exist */}
      {testimonials.length > 0 && (
        <section className="py-24 bg-gray-50" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Testimonials</p>
              <h2 className="font-heading font-bold text-4xl md:text-5xl text-[#0A0A0A]">What Our Clients Say</h2>
            </div>
            
            <div className={`grid gap-8 ${testimonials.length === 1 ? 'max-w-xl mx-auto' : testimonials.length === 2 ? 'md:grid-cols-2 max-w-4xl mx-auto' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
              {testimonials.slice(0, 6).map((testimonial, index) => (
                <div 
                  key={testimonial.id || index}
                  className="bg-white p-8 shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`testimonial-${index}`}
                >
                  <div className="flex mb-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        size={18}
                        className={star <= testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}
                        fill={star <= testimonial.rating ? 'currentColor' : 'none'}
                      />
                    ))}
                  </div>
                  <Quote className="text-[#3B82F6] opacity-20 mb-2" size={32} />
                  <p className="text-gray-700 mb-6 leading-relaxed">{testimonial.review}</p>
                  <div className="border-t pt-4">
                    <p className="font-heading font-bold text-[#0A0A0A]">{testimonial.customer_name}</p>
                    {testimonial.company && (
                      <p className="text-gray-500 text-sm">{testimonial.company}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Service Area Map - BLACK */}
      <section className="py-24 bg-[#273268]" data-testid="locations-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Service Area</p>
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-white">Serving All of Western Australia</h2>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* WA Map SVG */}
            <div className="relative">
              <svg viewBox="0 0 400 500" className="w-full max-w-md mx-auto">
                {/* WA State Outline */}
                <path
                  d="M50,50 L350,50 L350,100 L380,100 L380,450 L50,450 L50,200 L80,200 L80,150 L50,150 Z"
                  fill="#1e293b"
                  stroke="#3B82F6"
                  strokeWidth="2"
                  className="transition-all duration-300"
                />
                
                {/* Perth Metro - highlighted */}
                <circle cx="100" cy="300" r="25" fill="#3B82F6" opacity="0.8" className="animate-pulse" />
                <text x="100" y="305" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">PERTH</text>
                
                {/* Kalgoorlie */}
                <circle cx="280" cy="280" r="12" fill="#3B82F6" opacity="0.6" />
                <text x="280" y="260" textAnchor="middle" fill="#94a3b8" fontSize="9">Kalgoorlie</text>
                
                {/* Esperance */}
                <circle cx="300" cy="380" r="10" fill="#3B82F6" opacity="0.6" />
                <text x="300" y="400" textAnchor="middle" fill="#94a3b8" fontSize="9">Esperance</text>
                
                {/* Albany */}
                <circle cx="160" cy="420" r="10" fill="#3B82F6" opacity="0.6" />
                <text x="160" y="440" textAnchor="middle" fill="#94a3b8" fontSize="9">Albany</text>
                
                {/* Busselton */}
                <circle cx="90" cy="370" r="10" fill="#3B82F6" opacity="0.6" />
                <text x="90" y="390" textAnchor="middle" fill="#94a3b8" fontSize="9">Busselton</text>
                
                {/* Margaret River */}
                <circle cx="70" cy="400" r="8" fill="#3B82F6" opacity="0.6" />
                <text x="70" y="420" textAnchor="middle" fill="#94a3b8" fontSize="8">M. River</text>
                
                {/* Connection lines from Perth */}
                <line x1="120" y1="300" x2="268" y2="280" stroke="#3B82F6" strokeWidth="1" opacity="0.3" strokeDasharray="4" />
                <line x1="115" y1="315" x2="290" y2="375" stroke="#3B82F6" strokeWidth="1" opacity="0.3" strokeDasharray="4" />
                <line x1="105" y1="320" x2="155" y2="410" stroke="#3B82F6" strokeWidth="1" opacity="0.3" strokeDasharray="4" />
                <line x1="95" y1="320" x2="90" y2="360" stroke="#3B82F6" strokeWidth="1" opacity="0.3" strokeDasharray="4" />
              </svg>
            </div>
            
            {/* Service Areas List */}
            <div>
              <h3 className="font-heading font-bold text-2xl text-white mb-6">Areas We Service</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-white/5 border border-white/10 hover:bg-[#3B82F6]/20 transition-all">
                  <MapPin className="text-[#3B82F6]" size={24} />
                  <div>
                    <h4 className="font-bold text-white leading-relaxed">Perth Metropolitan Area</h4>
                    <p className="text-gray-400 text-sm">Complete coverage across all Perth suburbs</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-white/5 border border-white/10 hover:bg-[#3B82F6]/20 transition-all">
                  <MapPin className="text-[#3B82F6]" size={24} />
                  <div>
                    <h4 className="font-bold text-white leading-relaxed">Kalgoorlie-Boulder</h4>
                    <p className="text-gray-400 text-sm">Goldfields region commercial cleaning</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-white/5 border border-white/10 hover:bg-[#3B82F6]/20 transition-all">
                  <MapPin className="text-[#3B82F6]" size={24} />
                  <div>
                    <h4 className="font-bold text-white leading-relaxed">Geraldton & Mid West</h4>
                    <p className="text-gray-400 text-sm">Northern coastal commercial cleaning</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-white/5 border border-white/10 hover:bg-[#3B82F6]/20 transition-all">
                  <MapPin className="text-[#3B82F6]" size={24} />
                  <div>
                    <h4 className="font-bold text-white leading-relaxed">South West Region</h4>
                    <p className="text-gray-400 text-sm">Busselton, Margaret River, Bunbury, Australind & Albany</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-white/5 border border-white/10 hover:bg-[#3B82F6]/20 transition-all">
                  <MapPin className="text-[#3B82F6]" size={24} />
                  <div>
                    <h4 className="font-bold text-white leading-relaxed">Esperance & Great Southern</h4>
                    <p className="text-gray-400 text-sm">Regional commercial services</p>
                  </div>
                </div>
              </div>
              <p className="text-gray-500 text-sm mt-6">
                * We service all of Western Australia. Contact us for locations not listed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Common Questions FAQ */}
      <section className="py-24 bg-gray-50" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-[#0A0A0A]">
              Common Questions
            </h2>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-white border border-gray-200 rounded-sm overflow-hidden"
                data-testid={`faq-item-${index}`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                  aria-expanded={openFaq === index}
                >
                  <span className="font-heading font-bold text-lg text-[#0A0A0A] pr-4">
                    {faq.question}
                  </span>
                  {openFaq === index ? (
                    <ChevronUp className="text-[#3B82F6] flex-shrink-0" size={24} />
                  ) : (
                    <ChevronDown className="text-[#3B82F6] flex-shrink-0" size={24} />
                  )}
                </button>
                <div 
                  className={`overflow-hidden transition-all duration-300 ${
                    openFaq === index ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <p className="px-6 pb-6 text-gray-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - WHITE with BLUE accent */}
      <section className="py-24 bg-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-4xl md:text-5xl text-[#0A0A0A] mb-6">
            Ready for a Spotless Space?
          </h2>
          <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
            Get a free, no-obligation quote today. Our team will assess your needs and provide a customised cleaning solution.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={onQuoteClick}
              className="btn-primary flex items-center justify-center gap-2"
              data-testid="cta-quote-btn"
            >
              Get Free Quote
              <ArrowRight size={18} />
            </button>
            <a 
              href="tel:0861875523"
              className="btn-secondary"
              data-testid="cta-call-btn"
            >
              Call 08 6187 5523
            </a>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Home;
